import { Icon, Position, Tooltip, MenuItem as BpMenuItem, MenuDivider } from '@blueprintjs/core'
import cx from 'classnames'
import { lang } from 'enablex/shared'
import _ from 'lodash'
import React, { FC, useEffect, Fragment } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { generatePath, RouteComponentProps, withRouter } from 'react-router'
import { matchPath, Link } from 'react-router-dom'

import AccessControl from '~/auth/AccessControl'
import { getActiveWorkspace } from '~/auth/basicAuth'
import AndroidIcon from '~/icons/AndroidIcon'
import CodeEditorIcon from '~/icons/CodeEditorIcon'
import DepartmentIcon from '~/icons/DepartmentIcon'
import GlobeIcon from '~/icons/GlobeIcon'
import ModulesIcon from '~/icons/ModulesIcon'
import UserIcon from '~/icons/UserIcon'
import { addModuleIcon } from '~/workspace/bots/WorkspaceAppItems'
import { AppState } from '../rootReducer'
import style from './style.scss'
import BrandingIcon from '~/icons/BrandingIcon'
import ThemeIcon from '~/icons/ThemeIcon'
import AnalyticsIcon from '~/icons/AnalyticsIcon'

type Props = ConnectedProps<typeof connector> & RouteComponentProps & {
  profile?: any
  props?: any
}

interface MenuItemProps {
  text: string
  icon: any
  url: string
  id: string
  superAdmin?: boolean
  resource?: string
  operation?: 'read' | 'write'
  tag?: JSX.Element | undefined
}

const Menu: FC<Props> = props => {

  const { profile } = props
  const currentUserRole = localStorage.getItem('enx/channel-web/user-role')
  useEffect(() => {
    if (!props.version.currentVersion) {
      //props.fetchCurrentVersion()
    }

    // if (!props.version.latestReleases.length) {
    //   props.fetchLatestVersions()
    // }
  }, [])

  const MenuItem = ({ text, icon, url, id, operation, resource, superAdmin, tag }: MenuItemProps) => {
    const workspaceId = getActiveWorkspace()
    // Check if the menu item matches the current URL
    let active = matchPath(props.location.pathname, { path: url }) !== null

    // Bots menu should be active when accessing a bot-scoped workspace app
    if (id === 'btn-menu-bots' && matchPath(props.location.pathname, { path: '/apps/:appName/:botId', exact: true })) {
      active = true
    }
    if (id === 'btn-menu-sessions') {
      active = matchPath(props.location.pathname, { path: '/apps/:appName/:botId/sessions', exact: true }) !== null
    }

    return (
      <AccessControl resource={resource} operation={operation} superAdmin={superAdmin} key={text}>
        <li id={id} key={id}>
          <Tooltip
            boundary="window"
            position={Position.RIGHT}
            content={
              <div className={style.tooltipContent}>
                <span>{text}</span>
                {tag && <span className={style.tag}>{tag}</span>}
              </div>
            }
          >
            <Link
              className={cx({ [style.active]: active })}
              to={generatePath(url, { workspaceId: workspaceId || undefined })}
            >
              <Icon icon={icon} />
              <span>{text}</span>
              {tag && <span className={style.small_tag}>{tag}</span>}{' '}
            </Link>
          </Tooltip>
        </li>
      </AccessControl>
    )
  }

  const renderLatestReleaseTag = () => {
    const current = props.version.currentVersion
    const latest = _.get(props, 'version.latestReleases.0.version', current)
    if (latest > current) {
      return <span>new</span>
    }
  }
  const getIcon = (icon: any) => {
    if(icon.props.icon === 'code'){
      return <CodeEditorIcon color="#333"/>
    }
    return icon
  }

  const renderWorkspaceApps = () => {
    return (
      <Fragment>
        {props.loadedModules
          .filter(x => x.workspaceApp?.global)
          .map(addModuleIcon)
          .map(module => (
            <MenuItem
              key={module.name}
              id={`btn-menu-${module.name}`}
              text={module.menuText}
              icon={getIcon(module.menuIcon)}
              superAdmin={
                module.name === 'code-editor'
              }
              url={`/apps/${module.name}`}
              resource={`module.${module.name}`}
              operation="write"
            />
          ))}
      </Fragment>
    )
  }

  const isHitlNextPage = window.location.pathname.includes('apps/hitlnext')
const pathSegments = window.location.pathname.split('/')

// Find the bot ID dynamically (assuming it's the segment after "hitlnext" or "hitlnextsessions")
const hitlIndex = pathSegments.findIndex(segment => segment.includes('hitlnext'))
const botId = hitlIndex !== -1 && pathSegments[hitlIndex + 1] ? pathSegments[hitlIndex + 1] : null

const menuUrl = botId
  ? `/apps/hitlnextsessions/${botId}/sessions`
  : ''
  return (
    <aside className={cx(style.sidebar, 'bp-sidebar')}>
      <ul className='nav'>
        {profile?.isAgentUser ? (
          <MenuItem
            id="btn-menu-stats"
            text="Dashboard"
            icon={<ThemeIcon width="32" height="32" color="#333" />}
            url="/workspace/:workspaceId?/stats"
            resource="analytics.agent"
            operation="read"
          />
        ) : <></> }
        {currentUserRole === 'supervisor' ? (
          <MenuItem
            id="btn-menu-dashboard"
            text="Dashboard"
            icon={<AnalyticsIcon width="32" height="32" color="#333" />}
            url="/workspace/:workspaceId?/dashboard"
            resource="admin.collaborators.*"
            operation="read"
          />
        ) : <></> }
        <MenuItem
          id="btn-menu-bots"
          text={lang.tr('admin.sideMenu.bots')}
          icon={<AndroidIcon width="32" height="32" color="#333" />}
          url="/workspace/:workspaceId?/bots"
          resource="user.bots.*"
          operation="read"
        />
       {(profile?.isAgentUser || profile?.isSupervisor) && menuUrl ? (
          <MenuItem
            id="btn-menu-sessions"
            text="Sessions"
            icon={<AnalyticsIcon width="32" height="32" color="#333" />}
            url={menuUrl}
            resource="analytics.agent"
            operation="read"
          />
        ) : <></>}

        <MenuItem
          id="btn-menu-users"
          text={currentUserRole === 'supervisor' ? 'Agents' : lang.tr('admin.sideMenu.collaborators')}
          icon={<UserIcon width="32" height="32" color="#333"/>}
          url="/workspace/:workspaceId?/users"
          resource="admin.collaborators.*"
          operation="read"
        />

        {(profile?.isSuperAdmin ||
          (profile?.isAdminUser && !profile?.isEnablexAdmin) ||
          (profile?.isAdminUser && profile?.isEnablexAdmin)) && (
          <MenuItem
            id="btn-menu-departments"
            text="Departments"
            icon={<DepartmentIcon width={32} height={32} color="#333"/>}
            url="/workspace/:workspaceId?/departments"
            resource="admin.departments.*"
            operation="read"
          />
        )}

        {currentUserRole !== 'supervisor' && (
          <MenuItem
            id="btn-menu-branding"
            text={lang.tr('admin.sideMenu.branding')}
            icon={<BrandingIcon width="32" height="32" color="#333"/>}
            url="/workspace/:workspaceId?/branding"
            resource="admin.collaborators.*"
            operation="read"
          />
        )}

        {/* <MenuItem
          id="btn-menu-roles"
          text={lang.tr('admin.sideMenu.roles')}
          icon="shield"
          url="/workspace/:workspaceId?/roles"
          resource="admin.roles.*"
          operation="read"
        /> */}

        {/* <MenuItem
          id="btn-menu-logs"
          text={lang.tr('admin.sideMenu.logs')}
          icon="manual"
          url="/workspace/:workspaceId?/logs"
          resource="admin.logs"
          operation="read"
        /> */}

        {renderWorkspaceApps()}

        <AccessControl superAdmin={true}>
          {/* <MenuItem
            id="btn-menu-version"
            text={lang.tr('admin.sideMenu.sourceControl')}
            icon="changes"
            url="/server/version"
          /> */}
          <MenuItem
            text={lang.tr('admin.sideMenu.languages')}
            id="btn-menu-language"
            icon={<GlobeIcon color="#333" width="32" height="32"/>}
            url="/server/languages"
          />
          {/* <MenuItem text={lang.tr('sideMenu.modules')} id="btn-menu-modules" icon={<ModulesIcon  color="#1D6C90" width="32" height="32"/>} url="/modules" /> */}
          {/* <MenuItem
            text={lang.tr('admin.sideMenu.productionChecklist')}
            id="btn-menu-checklist"
            icon="endorsed"
            url="/checklist"
          />
          <MenuItem
            id="btn-menu-monitoring"
            text={lang.tr('admin.sideMenu.monitoring')}
            icon="timeline-line-chart"
            url="/server/monitoring"
          />
          <MenuItem
            id="btn-menu-alerting"
            text={lang.tr('admin.sideMenu.alerting')}
            icon="notifications"
            url="/server/alerting"
          /> */}
        </AccessControl>
        {/* <MenuItem
          text={lang.tr('admin.sideMenu.latestReleases')}
          id="btn-menu-releases"
          icon="feed"
          url="/latestReleases"
          tag={renderLatestReleaseTag()}
        /> */}
      </ul>
    </aside>
  )
}

const mapStateToProps = (state: AppState) => ({
  version: state.version,
  loadedModules: state.modules.loadedModules
})

const connector = connect(mapStateToProps, {})
export default withRouter(connector(Menu))
